import * as React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  CapacityConfirmationDialog,
  capacityConfirmationMessage,
} from '@/components/events/event-order-capacity-confirmation'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { QuantityStepper } from '@/components/ui/quantity-stepper'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ApiError, getJSON, postJSON } from '@/lib/api'
import type { Contact } from '@/lib/contacts/types'
import { cn } from '@/lib/utils'
import type { EventRecord } from '@/pages/event-types'

type CustomPaymentType = { id: number; name: string }
type PaymentChoice = 'invoice' | 'paid_now'
type PaymentType = { value: string; label: string; customId?: number }
type CreatedOrder = { id: number; status: string }

const BUILT_IN_PAYMENT_TYPES = ['cash', 'check', 'wire', 'other'].map((value) => ({
  value,
  label: value,
}))

export function EventOrderForm({
  event,
  onCancel,
  onCreated,
}: {
  event: Pick<EventRecord, 'id' | 'name' | 'pay_later_enabled' | 'ticket_types'>
  onCancel: () => void
  onCreated: (order: CreatedOrder) => void
}) {
  const [search, setSearch] = React.useState('')
  const [contact, setContact] = React.useState<Contact>()
  const [quantities, setQuantities] = React.useState<Record<number, number>>({})
  const [paymentChoice, setPaymentChoice] = React.useState<PaymentChoice>('invoice')
  const [paymentTypeValue, setPaymentTypeValue] = React.useState('cash')
  const [confirmation, setConfirmation] = React.useState<string>()
  const [suppressEmails, setSuppressEmails] = React.useState(false)
  const idempotencyKey = React.useRef(crypto.randomUUID())
  const contacts = useQuery({
    queryKey: ['staff-order-contacts', search],
    queryFn: () => getJSON<{ contacts: Contact[] }>(contactSearchPath(search)),
    retry: false,
  })
  const customTypes = useQuery({
    queryKey: ['custom-payment-types', 'available'],
    queryFn: () =>
      getJSON<{ custom_payment_types: CustomPaymentType[] }>(
        '/api/v1/custom_payment_types?available=true',
      ),
    retry: false,
  })
  const paymentTypes: PaymentType[] = [
    ...BUILT_IN_PAYMENT_TYPES,
    ...(customTypes.data?.custom_payment_types ?? []).map((type) => ({
      value: `custom:${type.id}`,
      label: type.name,
      customId: type.id,
    })),
  ]
  const createOrder = useMutation({
    mutationFn: (confirmOverCapacity: boolean) =>
      postJSON<{ order: CreatedOrder }>(`/api/v1/events/${event.id}/orders`, {
        order: orderPayload({
          contact,
          event,
          quantities,
          paymentChoice,
          paymentTypes,
          paymentTypeValue,
          idempotencyKey: idempotencyKey.current,
          suppressEmails,
          confirmOverCapacity,
        }),
      }),
    onSuccess: ({ order }) => {
      setConfirmation(order.status)
      onCreated(order)
    },
  })
  const confirmationMessage = capacityConfirmationMessage(createOrder.error)

  return (
    <form
      className="max-w-2xl space-y-5"
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault()
        createOrder.mutate(false)
      }}
    >
      {confirmation && <Alert variant="success">{confirmation}</Alert>}
      {contractErrors(createOrder.error).map((message) => (
        <Alert key={message}>{message}</Alert>
      ))}
      <ContactField
        search={search}
        onSearch={setSearch}
        contacts={contacts.data?.contacts ?? []}
        selected={contact}
        unavailable={contacts.isError}
        loading={contacts.isLoading}
        onSelect={setContact}
      />
      <TicketsField event={event} quantities={quantities} onChange={setQuantities} />
      <PaymentField
        choice={paymentChoice}
        onChoice={setPaymentChoice}
        types={paymentTypes}
        typeValue={paymentTypeValue}
        onTypeValue={setPaymentTypeValue}
      />
      <div className="flex items-start gap-2">
        <Checkbox
          id="staff-order-backfill"
          checked={suppressEmails}
          onCheckedChange={(checked) => setSuppressEmails(checked === true)}
        />
        <div className="space-y-0.5">
          <Label htmlFor="staff-order-backfill">Don't send emails (backfill)</Label>
          <p className="text-caption text-muted-foreground">
            Record this Order as history without emailing the buyer or Attendees.
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={createOrder.isPending}>
          {createOrder.isPending ? 'Creating…' : 'Create Order'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <CapacityConfirmationDialog
        message={confirmationMessage}
        pending={createOrder.isPending}
        onConfirm={() => createOrder.mutate(true)}
        onClose={() => createOrder.reset()}
      />
    </form>
  )
}

function ContactField({
  search,
  onSearch,
  contacts,
  selected,
  unavailable,
  loading,
  onSelect,
}: {
  search: string
  onSearch: (value: string) => void
  contacts: Contact[]
  selected?: Contact
  unavailable: boolean
  loading: boolean
  onSelect: (contact: Contact) => void
}) {
  return (
    <section className="space-y-2">
      <Label htmlFor="staff-order-contact-search">Contact</Label>
      <Input
        id="staff-order-contact-search"
        aria-label="Search Contacts"
        placeholder="Search name or email"
        value={search}
        onChange={(event) => onSearch(event.target.value)}
      />
      {unavailable && <Alert>Contacts unavailable</Alert>}
      {loading && (
        <p className="text-caption text-muted-foreground" role="status">
          Loading Contacts…
        </p>
      )}
      <div className="max-h-48 overflow-y-auto divide-y divide-border rounded-md border border-border bg-card">
        {contacts.map((contact) => (
          <Button
            key={contact.id}
            type="button"
            variant="ghost"
            className={cn(
              'h-auto w-full justify-start rounded-none px-3 py-2 text-left',
              selected?.id === contact.id && 'bg-primary/10 hover:bg-primary/15',
            )}
            aria-pressed={selected?.id === contact.id}
            onClick={() => onSelect(contact)}
          >
            <span>
              <span className="block text-body-sm font-medium">{contact.name}</span>
              <span className="block text-caption font-normal text-muted-foreground">
                {contact.email ?? 'No email'}
              </span>
            </span>
          </Button>
        ))}
        {!loading && !unavailable && contacts.length === 0 && (
          <p className="px-3 py-6 text-center text-caption text-muted-foreground">
            No Contacts found.
          </p>
        )}
      </div>
    </section>
  )
}

function TicketsField({
  event,
  quantities,
  onChange,
}: {
  event: Pick<EventRecord, 'ticket_types'>
  quantities: Record<number, number>
  onChange: React.Dispatch<React.SetStateAction<Record<number, number>>>
}) {
  return (
    <section className="space-y-2" aria-labelledby="staff-order-tickets">
      <h2 id="staff-order-tickets" className="text-body-sm font-medium">
        Tickets
      </h2>
      <div className="divide-y divide-border rounded-md border border-border bg-card">
        {event.ticket_types
          .filter((ticket) => ticket.active)
          .map((ticket) => (
            <div key={ticket.id} className="flex items-center justify-between gap-4 px-3 py-2.5">
              <div>
                <p className="text-body-sm font-medium">{ticket.name}</p>
                <p className="text-caption text-muted-foreground">
                  {ticket.price}
                  {ticket.audience === 'members'
                    ? ' · Members only · no Sign-in Code required'
                    : ''}
                </p>
              </div>
              <QuantityStepper
                label={ticket.name}
                value={quantities[ticket.id] ?? 0}
                min={0}
                max={ticket.max_per_order ?? undefined}
                onChange={(quantity) =>
                  onChange((current) => ({ ...current, [ticket.id]: quantity }))
                }
              />
            </div>
          ))}
      </div>
    </section>
  )
}

function PaymentField({
  choice,
  onChoice,
  types,
  typeValue,
  onTypeValue,
}: {
  choice: PaymentChoice
  onChoice: (choice: PaymentChoice) => void
  types: PaymentType[]
  typeValue: string
  onTypeValue: (value: string) => void
}) {
  return (
    <section className="space-y-2" aria-labelledby="staff-order-payment">
      <h2 id="staff-order-payment" className="text-body-sm font-medium">
        Payment
      </h2>
      <div className="grid gap-2 sm:grid-cols-2">
        <Button
          type="button"
          variant="outline"
          aria-pressed={choice === 'invoice'}
          className={cn(
            'h-auto justify-start px-3 py-2',
            choice === 'invoice' && 'border-primary bg-primary/10',
          )}
          onClick={() => onChoice('invoice')}
        >
          Invoice
        </Button>
        <Button
          type="button"
          variant="outline"
          aria-pressed={choice === 'paid_now'}
          className={cn(
            'h-auto justify-start px-3 py-2',
            choice === 'paid_now' && 'border-primary bg-primary/10',
          )}
          onClick={() => onChoice('paid_now')}
        >
          Paid now
        </Button>
      </div>
      {choice === 'paid_now' && (
        <div className="max-w-sm space-y-1.5">
          <Label htmlFor="staff-order-payment-type">Payment Type</Label>
          <Select value={typeValue} onValueChange={onTypeValue}>
            <SelectTrigger id="staff-order-payment-type" aria-label="Payment Type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {types.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </section>
  )
}

function contactSearchPath(search: string) {
  const params = new URLSearchParams()
  if (search.trim()) params.set('search', search.trim())
  return `/api/v1/contacts?${params}`
}

function orderPayload({
  contact,
  event,
  quantities,
  paymentChoice,
  paymentTypes,
  paymentTypeValue,
  idempotencyKey,
  suppressEmails,
  confirmOverCapacity,
}: {
  contact?: Contact
  event: Pick<EventRecord, 'ticket_types'>
  quantities: Record<number, number>
  paymentChoice: PaymentChoice
  paymentTypes: PaymentType[]
  paymentTypeValue: string
  idempotencyKey: string
  suppressEmails: boolean
  confirmOverCapacity: boolean
}) {
  const selections = event.ticket_types
    .map((ticket) => ({ ticket_type_id: ticket.id, quantity: quantities[ticket.id] ?? 0 }))
    .filter(({ quantity }) => quantity > 0)
  const selectedPaymentType = paymentTypes.find(({ value }) => value === paymentTypeValue)
  return {
    contact_id: contact?.id,
    selections,
    attendees: [],
    answers: [],
    payment_option: paymentChoice,
    suppress_emails: suppressEmails,
    ...(confirmOverCapacity ? { confirm_over_capacity: true } : {}),
    ...(paymentChoice === 'paid_now' && selectedPaymentType?.customId
      ? { custom_payment_type_id: selectedPaymentType.customId }
      : {}),
    ...(paymentChoice === 'paid_now' && !selectedPaymentType?.customId
      ? { payment_method: paymentTypeValue }
      : {}),
    idempotency_key: idempotencyKey,
  }
}

function contractErrors(error: Error | null) {
  if (capacityConfirmationMessage(error)) return []
  if (
    !(error instanceof ApiError) ||
    typeof error.body !== 'object' ||
    error.body === null ||
    !('errors' in error.body)
  )
    return error ? [error.message] : []
  const errors = (error.body as { errors: Record<string, string[]> }).errors
  return Object.values(errors).flat()
}
